<template>
  <div
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      style="position: relative; display: inline-block;"
  >
    <slot/>

    <transition name="tooltip-fade">
      <div
          v-if="isTooltipVisible"
          class="tooltip-balloon"
          :style="tooltipStyle"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave">
        <slot name="tooltip-content"/>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HoverTooltip',
  props: {
    maxHeight: {
      type: String,
      default: '200px',
    },
    hideDelay: {
      type: Number,
      default: 150,
    },
  },
  data() {
    return {
      isTooltipVisible: false,
      hideTimeoutId: null,
    };
  },
  computed: {
    tooltipStyle() {
      return {
        maxHeight: this.maxHeight,
      };
    },
  },
  methods: {
    showTooltip() {
      this.clearHideTimeout();
      this.isTooltipVisible = true;
    },
    startHideTimeout() {
      this.clearHideTimeout();
      this.hideTimeoutId = setTimeout(() => {
        this.isTooltipVisible = false;
      }, this.hideDelay);
    },
    clearHideTimeout() {
      if (this.hideTimeoutId) {
        clearTimeout(this.hideTimeoutId);
        this.hideTimeoutId = null;
      }
    },
    handleMouseEnter() {
      this.showTooltip();
    },
    handleMouseLeave() {
      this.startHideTimeout();
    },
  },
  beforeDestroy() {
    this.clearHideTimeout();
  },
};
</script>

<style scoped lang="scss">
$tooltip-bg-color: rgba(0, 0, 0, 0.8);
$tooltip-text-color: white;
$tooltip-padding: 10px 15px;
$tooltip-border-radius: 5px;
$tooltip-arrow-size: 6px;
$tooltip-margin-bottom: 8px; // Espaço entre gatilho e balão
$tooltip-z-index: 1000;
$tooltip-min-width: 150px;
$tooltip-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

$scrollbar-width: 6px;
$scrollbar-thumb-color: rgba(255, 255, 255, 0.5);
$scrollbar-track-color: rgba(0, 0, 0, 0.1); // Cor mais clara para webkit track
$scrollbar-track-color-ff: rgba(0, 0, 0, 0.3); // Cor usada no exemplo original FF

$transition-duration: 0.2s;
$transition-timing-function: ease;

// --- Estilos ---

.tooltip-balloon {
  // Posicionamento
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: $tooltip-margin-bottom;
  z-index: $tooltip-z-index;
  cursor: default;

  // Estilo do Balão
  background-color: $tooltip-bg-color;
  color: $tooltip-text-color;
  padding: $tooltip-padding;
  border-radius: $tooltip-border-radius;
  box-shadow: $tooltip-shadow;
  min-width: $tooltip-min-width;
  box-sizing: border-box;

  // Controle de Altura e Rolagem
  overflow-y: auto; // Mostra a barra de rolagem vertical SE necessário
  // max-height é aplicado via :style="tooltipStyle" no template

  // Estilização da barra de rolagem (Firefox)
  scrollbar-width: thin;
  scrollbar-color: $scrollbar-thumb-color $scrollbar-track-color-ff;

  // Estilização da barra de rolagem (Webkit/Blink - Chrome, Edge, Safari)
  &::-webkit-scrollbar {
    width: $scrollbar-width;
  }

  &::-webkit-scrollbar-track {
    background: $scrollbar-track-color;
    border-radius: calc(#{$scrollbar-width} / 2); // Metade da largura da barra
  }

  &::-webkit-scrollbar-thumb {
    background-color: $scrollbar-thumb-color;
    border-radius: calc(#{$scrollbar-width} / 2);
  }

  // Seta abaixo do balão (usando o seletor pai '&')
  &::after {
    content: '';
    position: absolute;
    top: 100%; // Abaixo do balão
    left: 50%;
    transform: translateX(-50%);
    border-width: $tooltip-arrow-size;
    border-style: solid;
    // A cor da seta deve ser a mesma do fundo do balão
    border-color: $tooltip-bg-color transparent transparent transparent;
  }
}

// Transição de Fade (fora do aninhamento principal pois são classes de transição do Vue)
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity $transition-duration $transition-timing-function;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to { // .tooltip-fade-leave-active em <2.1.8
  opacity: 0;
}
</style>